import React, { useState, useEffect } from 'react';
import { X, Check, Megaphone, Loader2, ExternalLink, Sparkles, ChevronRight } from 'lucide-react';
import { Language, Notice } from '../../types';
import { t } from '../../i18n';
import { fetchActiveNotice } from '../../services/adminService';
import { formatLocalDateTime } from '../../services/formatService';

interface NoticeModalProps {
  lang: Language;
}

const NOTICE_STORAGE_KEY_PREFIX = 'xleven_notice_expiry_';

export const NoticeModal: React.FC<NoticeModalProps> = ({ lang }) => {
  const [activeNotice, setActiveNotice] = useState<Notice | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [dontShowToday, setDontShowToday] = useState(false);

  useEffect(() => {
    const loadNotice = async () => {
      try {
        const notice = await fetchActiveNotice();
        if (notice) {
          const expiry = localStorage.getItem(`${NOTICE_STORAGE_KEY_PREFIX}${notice.id}`);
          const now = new Date().getTime();

          if (!expiry || now > parseInt(expiry)) {
            setActiveNotice(notice);
            setIsOpen(true);
            // 소폭의 지연 후 애니메이션 시작
            setTimeout(() => setIsAnimating(true), 50);
          }
        }
      } catch (e) {
        console.warn("Notice loading failed", e);
      }
    };
    loadNotice();
  }, []);

  const handleClose = () => {
    setIsAnimating(false);
    
    if (dontShowToday && activeNotice) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      localStorage.setItem(`${NOTICE_STORAGE_KEY_PREFIX}${activeNotice.id}`, tomorrow.getTime().toString());
    }

    setTimeout(() => setIsOpen(false), 500);
  };

  if (!isOpen || !activeNotice) return null;

  const title = 
    lang === 'ko' ? activeNotice.title_ko : 
    // lang === 'ja' ? (activeNotice.title_ja || activeNotice.title_en) :
    // lang === 'es' ? (activeNotice.title_es || activeNotice.title_en) :
    // lang === 'fr' ? (activeNotice.title_fr || activeNotice.title_en) :
    activeNotice.title_en;

  const content = 
    lang === 'ko' ? activeNotice.content_ko : 
    // lang === 'ja' ? (activeNotice.content_ja || activeNotice.content_en) :
    // lang === 'es' ? (activeNotice.content_es || activeNotice.content_en) :
    // lang === 'fr' ? (activeNotice.content_fr || activeNotice.content_en) :
    activeNotice.content_en;

  return (
    <div className={`fixed inset-0 z-[500] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose}></div>

      {/* Modal Container - md 미만 바텀 시트 (100%), md 이상 카드 팝업 */}
      <div className={`relative w-full md:max-w-[420px] bg-white rounded-t-[40px] md:rounded-[48px] overflow-hidden shadow-2xl transition-transform duration-700 ease-[0.32,0.72,0,1] ${
          isAnimating 
            ? 'translate-y-0 md:scale-100' 
            : 'translate-y-full md:translate-y-12 md:scale-95'
        }`}>
        
        {/* Mobile Handle Bar */}
        <div className="w-12 h-1.5 bg-slate-100/20 rounded-full mx-auto my-4 md:hidden absolute top-0 left-0 right-0 z-20"></div>

        {/* Top Content Area: Colorful Gradient & Visuals */}
        <div className="relative p-8 pt-12 pb-12 min-h-[280px] md:min-h-[260px] overflow-hidden">
            {/* Background Decoration Icons */}
            <div className="absolute -right-6 -top-6 opacity-10 rotate-12">
                <Sparkles size={120} className="text-white" />
            </div>
            <div className="absolute -left-10 bottom-10 opacity-5 -rotate-12">
                <Megaphone size={100} className="text-white" />
            </div>

            <div className="relative z-10 space-y-6 pr-[5%]">
                <div className="space-y-1">
                    <div className="px-2 py-0.5 bg-white/20 w-fit rounded-lg mb-2">
                        <span className="text-[9px] font-black text-white uppercase tracking-widest">{t(lang, 'noticeUpdate')}</span>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-black text-white uppercase tracking-tight leading-tight whitespace-pre-wrap drop-shadow-md">
                        {title}
                    </h3>
                </div>
                
                <p className="text-blue-50 text-sm md:text-base font-medium leading-relaxed line-clamp-4 whitespace-pre-wrap">
                    {content}
                </p>

                {/* Call to Action Button */}
                {/* <button 
                  onClick={handleClose}
                  className="flex items-center gap-2 group text-white border-b-2 border-white/30 pb-1 md:hover:border-white transition-all w-fit"
                >
                    <span className="text-[10px] font-black uppercase tracking-[0.2em]">{t(lang, 'checkNow')}</span>
                    <ChevronRight size={14} strokeWidth={3} className="md:group-hover:translate-x-1 transition-transform" />
                </button> */}
            </div>

            {/* Main Visual Graphic */}
            <div className="absolute left-0 top-0 w-full h-full -z-10 bg-slate-900">
                {activeNotice.banner ? (
                    <img src={activeNotice.banner} className="w-full h-full object-cover opacity-60" alt="" />
                ) : (
                    <div className="w-full h-full bg-gradient-to-br from-rose-500 to-indigo-600 opacity-80" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
            </div>
        </div>

        {/* Bottom Action Bar */}
        <div className="flex border-t border-gray-100 h-20 md:h-16 bg-white">
            <button 
                onClick={() => setDontShowToday(!dontShowToday)}
                className="flex-1 flex items-center justify-center gap-2.5 md:hover:bg-gray-50 transition-colors border-r border-gray-100"
            >
                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${dontShowToday ? 'bg-slate-900 border-slate-900 text-white' : 'border-slate-200 text-transparent'}`}>
                    <Check size={12} strokeWidth={4} />
                </div>
                <span className={`text-[11px] font-black uppercase tracking-tight ${dontShowToday ? 'text-slate-900' : 'text-slate-400'}`}>
                    {t(lang, 'dontShowToday')}
                </span>
            </button>
            
            <button 
                onClick={handleClose}
                className="flex-1 text-[11px] font-black uppercase tracking-tight text-slate-900 md:hover:bg-gray-50 transition-colors"
            >
                {t(lang, 'close')}
            </button>
        </div>
      </div>
    </div>
  );
};